import { Button, ButtonTheme } from 'components/Button';
import { Logo } from 'components/Logo';
import { LogoAppStoreGoogle } from 'components/LogoAppStoreGoogle';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useEsc } from 'hooks/useEsc';
import { theme } from 'lib/theme';
import * as React from 'react';
import { Linking, Platform, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  CHECK_FOR_APP_UPDATE_MANUAL,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  PAUSE_BG_NETWORK,
  UNPAUSE_BG_NETWORK,
} from 'store/actions';
import { appStoreUrlApple, appStoreUrlGoogle } from 'lib/riskbase';
import { Pressable } from 'components/Pressable';
import { LogoAppStoreApple } from 'components/LogoAppStoreApple';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { ICONS } from 'components/Icon';
import { Page } from 'components/Page';
import { NavigationHeader } from 'components/NavigationHeader';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const UpgradeRequiredModal: React.FC<
  ModalScreenProps<ModalScreens.UpgradeRequired>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const { bottom } = useSafeAreaInsets();
  const { major, withClose } = route.params;

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: withClose });
    dispatch(PAUSE_BG_NETWORK());
    return () => {
      dispatch(UNPAUSE_BG_NETWORK());
    };
  }, [dispatch, navigation, withClose]);

  useEsc(() => (withClose ? dispatch(NAVIGATE_MODAL_BACK()) : undefined));

  const platform = Platform.OS;

  return (
    <Page
      contentPaddingTop={0}
      ignoreSafeAreaBottom
      header={
        <NavigationHeader
          onPressCloseButton={
            withClose ? () => dispatch(NAVIGATE_MODAL_BACK()) : undefined
          }
          heading="RiskBase Update Available"
        />
      }
    >
      <View style={{ flex: 1, justifyContent: 'center' }}>
        <ViewWithSeparators
          style={{
            justifyContent: 'center',
            padding: theme.paddingVertical,
          }}
          separatorColor="transparent"
          separatorMargin={theme.paddingVertical}
        >
          <Logo height={40} style={{ alignSelf: 'center' }} />

          {!major && (
            <Txt style={{ textAlign: 'center' }}>
              {platform === 'ios' || platform === 'android'
                ? 'Please update to continue using RiskBase;\nit only takes a few seconds'
                : 'Please refresh your browser window\nto continue using RiskBase'}
            </Txt>
          )}

          {!major && (platform === 'ios' || platform === 'android') && (
            <Button
              text="Check For Update"
              onPress={() => dispatch(CHECK_FOR_APP_UPDATE_MANUAL())}
              buttonTheme={ButtonTheme.themeless}
              style={{
                alignSelf: 'center',
              }}
            />
          )}

          {major && (
            <Txt style={{ textAlign: 'center' }}>
              {platform === 'ios'
                ? 'Please download the latest version of\nRiskBase from the Apple App Store'
                : platform === 'android'
                  ? 'Please download the latest version of\nRiskBase from the Google Play Store'
                  : 'Please refresh your browser window\nto continue using RiskBase'}
            </Txt>
          )}

          {major &&
            (platform === 'ios' ? (
              <Pressable
                onPress={() => Linking.openURL(appStoreUrlApple)}
                accessibilityHint="Opens RiskBase in the Apple App Store"
                style={{
                  alignSelf: 'center',
                  backgroundColor: 'black',
                  borderRadius: 4,
                  width: 140,
                  alignItems: 'center',
                }}
              >
                <LogoAppStoreApple height={40} />
              </Pressable>
            ) : platform === 'android' ? (
              <Pressable
                onPress={() => Linking.openURL(appStoreUrlGoogle)}
                accessibilityHint="Opens RiskBase in the Google Play Store"
                style={{
                  alignSelf: 'center',
                  backgroundColor: 'black',
                  borderRadius: 4,
                  width: 140,
                  alignItems: 'center',
                }}
              >
                <LogoAppStoreGoogle height={40} />
              </Pressable>
            ) : (
              ''
            ))}
        </ViewWithSeparators>
      </View>
      <SeparatorHorizontal />
      <View
        style={{
          padding: theme.paddingHorizontal,
          paddingBottom: theme.paddingHorizontal + bottom,
        }}
      >
        <Button
          text="Need Help? Get Support"
          icon={ICONS.LifeRingLight}
          onPress={() =>
            dispatch(
              NAVIGATE_MODAL({ screen: ModalScreens.Support, params: {} }),
            )
          }
          buttonTheme={ButtonTheme.themeless}
          style={{
            alignSelf: 'center',
          }}
        />
      </View>
    </Page>
  );
};
